import React, { useState } from "react";
import { Form, Button, Container, Row, Col, Alert, Card, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  createUser,
  getUserById,
  updateUser,
  checkExistingEmailOrUsername,
  forgotPassword,
  sendOTP,
} from "../services/UserService";
import UserData from "../models/UserData";
import OTPVerificationModal from "./OTPVerificationModal";
import { validateRequired, validateEmail, validatePhone, validatePassword, validateConfirmPassword, isValidEmail, isValidPhone } from "../utils/validation";

interface RegistrationProps {
  isTabMode?: boolean;
  onSwitchToLogin?: () => void;
}

function Registration({ isTabMode = false, onSwitchToLogin }: RegistrationProps) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData>({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    isEmailVerified: false,
    isPhoneVerified: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpType, setOtpType] = useState<'email' | 'mobile'>('email');
  const [otpValue, setOtpValue] = useState('');
  const [sendingOTP, setSendingOTP] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setUserData({ ...userData, [id]: value });
  };
  const clearFrom = () => {
    setUserData({
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      isEmailVerified: false,
      isPhoneVerified: false,
    });
  };
  const generateUsername = (firstName: string, lastName: string) => {
    const randomNum = Math.floor(Math.random() * 9999) + 1;
    return `${firstName.toLowerCase()}_${lastName.toLowerCase()}_${randomNum}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Auto-generate username
    const generatedUsername = generateUsername(userData.firstName||"", userData.lastName||"");
    const submitData = { ...userData, username: generatedUsername };

    // Validation checks
    const firstNameValidation = validateRequired(submitData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      setError(firstNameValidation.message!);
      return;
    }

    const lastNameValidation = validateRequired(submitData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      setError(lastNameValidation.message!);
      return;
    }

    const emailValidation = validateEmail(submitData.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message!);
      return;
    }

    const phoneValidation = validatePhone(submitData.phone);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message!);
      return;
    }

    const passwordValidation = validatePassword(submitData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message!);
      return;
    }

    const confirmPasswordValidation = validateConfirmPassword(submitData.password, submitData.confirmPassword);
    if (!confirmPasswordValidation.isValid) {
      setError(confirmPasswordValidation.message!);
      return;
    }

    // Check if at least one verification is completed
    if (!submitData.isEmailVerified && !submitData.isPhoneVerified) {
      setError("Please verify at least one contact method (email or mobile).");
      return;
    }

    // If all validation passes, you can proceed with registration logic
    try {
      await createUser(submitData);
      setSuccess("Registration successful!");
      setError(null);
      clearFrom();
    } catch (error: any) {
      if (error.response?.status === 400 || error.message?.includes('duplicate') || error.message?.includes('E11000')) {
        setError("Email or phone number already exists. Please use different credentials.");
      } else {
        setError(error.response?.data?.message || "Registration failed. Please try again.");
      }
      setSuccess(null);
    }
  };


  const handleSendOTP = async (type: 'email' | 'mobile') => {
    const value = type === 'email' ? userData.email : userData.phone;
    
    if (!value) {
      setError(`Please enter ${type === 'email' ? 'email address' : 'phone number'} first`);
      return;
    }

    if (type === 'email' && !isValidEmail(value)) {
      setError('Please enter a valid email address');
      return;
    }

    if (type === 'mobile' && !isValidPhone(value)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setSendingOTP(true);
    setError(null);

    try {
      await sendOTP(type, value);
      setOtpType(type);
      setOtpValue(value);
      setShowOTPModal(true);
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to send OTP to ${type}`);
    } finally {
      setSendingOTP(false);
    }
  };

  const handleOTPSuccess = () => {
    if (otpType === 'email') {
      setUserData({ ...userData, isEmailVerified: true });
    } else {
      setUserData({ ...userData, isPhoneVerified: true });
    }
    setSuccess(`${otpType === 'email' ? 'Email' : 'Mobile'} verified successfully!`);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '10rem'
    }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h3 className="fw-bold" style={{ color: '#810081' }}>Register To Natakiya</h3>
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="firstName">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter first name"
                          value={userData.firstName}
                          onChange={handleChange}
                          required
                          className="py-2 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            backgroundColor: '#fefefe'
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="lastName">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter last name"
                          value={userData.lastName}
                          onChange={handleChange}
                          required
                          className="py-2 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            backgroundColor: '#fefefe'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="email">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                          Email Address
                          {userData.isEmailVerified && (
                            <i className="bi bi-check-circle-fill text-success ms-2"></i>
                          )}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="email"
                            placeholder="Enter email"
                            value={userData.email}
                            onChange={handleChange}
                            required
                            readOnly={userData.isEmailVerified}
                            className="py-2 border-2"
                            style={{ 
                              borderRadius: '10px 0 0 10px', 
                              borderColor: '#FFD600',
                              backgroundColor: userData.isEmailVerified ? '#f8f9fa' : '#fefefe'
                            }}
                          />
                          <Button
                            variant="outline-primary"
                            onClick={() => handleSendOTP('email')}
                            disabled={sendingOTP || userData.isEmailVerified || !isValidEmail(userData.email || '') || !userData.email}
                            style={{
                              borderRadius: '0 10px 10px 0',
                              borderColor: '#FFD600',
                              color: '#810081'
                            }}
                          >
                            {userData.isEmailVerified ? 'Verified' : (sendingOTP ? 'Sending...' : 'Verify')}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="phone">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                          Phone Number
                          {userData.isPhoneVerified && (
                            <i className="bi bi-check-circle-fill text-success ms-2"></i>
                          )}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Enter phone number"
                            value={userData.phone}
                            onChange={handleChange}
                            required
                            readOnly={userData.isPhoneVerified}
                            className="py-2 border-2"
                            style={{ 
                              borderRadius: '10px 0 0 10px', 
                              borderColor: '#FFD600',
                              backgroundColor: userData.isPhoneVerified ? '#f8f9fa' : '#fefefe'
                            }}
                          />
                          <Button
                            variant="outline-primary"
                            onClick={() => handleSendOTP('mobile')}
                            disabled={sendingOTP || userData.isPhoneVerified || !isValidPhone(userData.phone || '') || !userData.phone}
                            style={{
                              borderRadius: '0 10px 10px 0',
                              borderColor: '#FFD600',
                              color: '#810081'
                            }}
                          >
                            {userData.isPhoneVerified ? 'Verified' : (sendingOTP ? 'Sending...' : 'Verify')}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="password">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Enter password"
                          value={userData.password}
                          onChange={handleChange}
                          required
                          className="py-2 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            backgroundColor: '#fefefe'
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="confirmPassword">
                        <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Confirm Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Confirm password"
                          value={userData.confirmPassword}
                          onChange={handleChange}
                          required
                          className="py-2 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            backgroundColor: '#fefefe'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      At least one contact method (email or mobile) must be verified to register.
                    </small>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-100 py-2 fw-semibold border-0 mb-3"
                    disabled={!userData.isEmailVerified && !userData.isPhoneVerified}
                    style={{
                      backgroundColor: (!userData.isEmailVerified && !userData.isPhoneVerified) ? '#ccc' : '#FFD600',
                      color: '#810081',
                      borderRadius: '10px',
                      fontSize: '16px'
                    }}
                  >
                    <i className="bi bi-person-check me-2"></i>
                    Register
                  </Button>
                </Form>

                {success && (
                  <Alert variant="success" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                    <i className="bi bi-check-circle me-2"></i>
                    {success}
                  </Alert>
                )}
                {error && (
                  <Alert variant="danger" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

               
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      
      <OTPVerificationModal
        show={showOTPModal}
        onHide={() => setShowOTPModal(false)}
        onSuccess={handleOTPSuccess}
        value={otpValue}
        type={otpType}
      />
    </div>
  );
}

export default Registration;
